// src/components/CalculationAssumptionsPage.js
import React, { useState, useMemo } from "react";
import { INDICATOR_OPTIONS } from "../indicators/engine";
import { getIndicatorDescription, Z_SCORE_EXPLANATION } from "../indicators/indicatorMeta";
import {
  getZScoreYearsForIndicator,
  isZScoreConfigurable,
  loadZScoreYearsMap,
} from "../indicators/zScoreConfig";

const assumptionsZScoreYearsMap = loadZScoreYearsMap();

const BRAND = {
  bg: "#0a0a0f",
  panel: "#1a1a24",
  panelHover: "#22222f",
  border: "#2a2a3a",
  text: "#e8e8f0",
  textSecondary: "#b8b8c8",
  muted: "#888898",
  accent: "#6366f1",
  accentHover: "#818cf8",
  success: "#10b981",
  danger: "#ef4444",
};

// Order of indicators to display (matches app usage)
const INDICATOR_ORDER = [
  "ev_ebitda", "ev_ebitda_z", "div_yield", "div_yield_z",
  "pb_ratio",
  "pe_ratio",
  "poi_ratio",
  "ps_ratio",
  "cf_price",
  "peg_ratio",
  "mean_rev", "syo", "rmo", "tamo", "nimo",
  "vmz", "oey", "vmo", "mrvz", "vddm", "nupl", "realized_price_252", "realized_price_504", "realized_price_5y",
  "risk", "srm", "pe_peratio", "forward_pe", "ttm_pe", "revenue_cagr", "eps_cagr", "fgpz", "gag", "rev_val", "eps_val",
];

function IndicatorCard({ id, meta, isExpanded, onToggle }) {
  const hasDetails = meta.how || meta.when || meta.intuition || (meta.pros && meta.pros.length) || (meta.cons && meta.cons.length) || meta.usesZScore;

  return (
    <div
      style={{
        background: BRAND.panel,
        border: `1px solid ${BRAND.border}`,
        borderRadius: 12,
        overflow: "hidden",
        marginBottom: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
      }}
    >
      <button
        type="button"
        onClick={() => hasDetails && onToggle(id)}
        style={{
          width: "100%",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "transparent",
          border: "none",
          color: BRAND.text,
          cursor: hasDetails ? "pointer" : "default",
          textAlign: "left",
          transition: "background 0.2s",
        }}
        onMouseEnter={(e) => hasDetails && (e.currentTarget.style.background = BRAND.panelHover)}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>
          {meta.title}
        </h2>
        {hasDetails && (
          <span style={{ color: BRAND.muted, fontSize: "0.9rem" }}>
            {isExpanded ? "▼" : "▶"}
          </span>
        )}
      </button>
      <div style={{ padding: "0 20px 16px" }}>
        <p style={{ margin: "0 0 12px", color: BRAND.textSecondary, fontSize: "0.95rem", lineHeight: 1.5 }}>
          {meta.short}
        </p>
        {isExpanded && hasDetails && (
          <div style={{ borderTop: `1px solid ${BRAND.border}`, paddingTop: 16, marginTop: 8 }}>
            {meta.intuition && (
              <section style={{ marginBottom: 16 }}>
                <h3 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: BRAND.accent, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Intuition
                </h3>
                <p style={{ margin: 0, color: BRAND.textSecondary, fontSize: "0.9rem", lineHeight: 1.5 }}>
                  {meta.intuition}
                </p>
              </section>
            )}
            {(meta.how || meta.usesZScore) && (
              <section style={{ marginBottom: 16 }}>
                <h3 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: BRAND.accent, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Calculation
                </h3>
                {meta.usesZScore && (
                  <p style={{ margin: "0 0 12px", color: BRAND.textSecondary, fontSize: "0.9rem", lineHeight: 1.5, fontStyle: "italic" }}>
                    <strong>What is a Z-score?</strong> {Z_SCORE_EXPLANATION}
                  </p>
                )}
                {meta.how && (
                  <p style={{ margin: 0, color: BRAND.textSecondary, fontSize: "0.9rem", lineHeight: 1.5 }}>
                    {meta.how}
                  </p>
                )}
              </section>
            )}
            {meta.when && (
              <section style={{ marginBottom: 16 }}>
                <h3 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: BRAND.accent, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  When to use
                </h3>
                <p style={{ margin: 0, color: BRAND.textSecondary, fontSize: "0.9rem", lineHeight: 1.5 }}>
                  {meta.when}
                </p>
              </section>
            )}
            {(meta.pros?.length > 0 || meta.cons?.length > 0) && (
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                {meta.pros?.length > 0 && (
                  <section style={{ flex: "1 1 200px" }}>
                    <h3 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: BRAND.success, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Pros
                    </h3>
                    <ul style={{ margin: 0, paddingLeft: 20, color: BRAND.textSecondary, fontSize: "0.9rem", lineHeight: 1.6 }}>
                      {meta.pros.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  </section>
                )}
                {meta.cons?.length > 0 && (
                  <section style={{ flex: "1 1 200px" }}>
                    <h3 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: BRAND.danger, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Cons / caveats
                    </h3>
                    <ul style={{ margin: 0, paddingLeft: 20, color: BRAND.textSecondary, fontSize: "0.9rem", lineHeight: 1.6 }}>
                      {meta.cons.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            )}
            {meta.thresholds && (
              <section style={{ marginTop: 16 }}>
                <h3 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: BRAND.accent, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Thresholds
                </h3>
                <pre style={{ margin: 0, padding: 12, background: "rgba(0,0,0,0.2)", borderRadius: 8, fontSize: "0.8rem", color: BRAND.textSecondary, overflow: "auto" }}>
                  {JSON.stringify(meta.thresholds, null, 2)}
                </pre>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CalculationAssumptionsPage() {
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState("");

  const indicators = useMemo(() => {
    const list = INDICATOR_ORDER.map((key) => ({
      key,
      meta: getIndicatorDescription(key, isZScoreConfigurable(key)
        ? { zScoreYears: getZScoreYearsForIndicator(key, assumptionsZScoreYearsMap) }
        : undefined),
    })).filter(({ meta }) => meta.title && meta.short);
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(
      ({ key, meta }) =>
        key.toLowerCase().includes(q) ||
        (meta.title && meta.title.toLowerCase().includes(q)) ||
        (meta.short && meta.short.toLowerCase().includes(q)) ||
        (meta.how && meta.how.toLowerCase().includes(q))
    );
  }, [search]);

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 16px" }}>
      <h1
        style={{
          margin: "0 0 8px",
          fontSize: "clamp(1.5rem, 4vw, 2rem)",
          fontWeight: 700,
          background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        Calculation & Assumptions
      </h1>
      <p style={{ margin: "0 0 24px", color: BRAND.textSecondary, fontSize: "0.95rem" }}>
        How each indicator is defined, calculated, and when to use it.
      </p>
      <input
        type="search"
        placeholder="Search indicators…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: "100%",
          maxWidth: 400,
          marginBottom: 24,
          padding: "12px 16px",
          background: BRAND.panel,
          border: `1px solid ${BRAND.border}`,
          borderRadius: 10,
          color: BRAND.text,
          fontSize: "1rem",
        }}
        aria-label="Search indicators"
      />
      <div>
        {indicators.map(({ key, meta }) => (
          <IndicatorCard
            key={key}
            id={key}
            meta={meta}
            isExpanded={expandedId === key}
            onToggle={toggle}
          />
        ))}
      </div>
      {indicators.length === 0 && (
        <p style={{ color: BRAND.muted, textAlign: "center", padding: 32 }}>
          No indicators match your search.
        </p>
      )}
    </div>
  );
}
